import React, { useState } from 'react';
import { Heart, XCircle, EnvelopeSimple, Phone, Flag, CheckCircle, Circle, CaretDown, Sparkle, Check, Clock } from '@phosphor-icons/react';
import { OfferStatusPill } from './OfferStatusPill';
import { OfferListingPreview } from './OfferListingPreview';
import { OfferPhotoGrid } from './OfferPhotoGrid';
import { NextStepsChecklist } from './NextStepsChecklist';
import { cn, isOfferChecklistComplete } from '../../lib/utils';
import type { OfferData } from './OfferCard';
import type { OfferStatus } from '../../lib/utils';
interface ReceivedOfferCardProps {
  offer: OfferData;
  tenantName?: string;
  onAccept?: () => void; // Salvează
  onReject?: () => void;
  onPostpone?: () => void;
  onStatusChange?: (newStatus: OfferStatus) => void;
  onMarkSucces?: () => void;
  /** Called when the tenant clicks the supply name in the succes state. */
  onViewSupplyProfile?: () => void;
}
export function ReceivedOfferCard({
  offer,
  tenantName,
  onAccept,
  onReject,
  onPostpone,
  onStatusChange,
  onMarkSucces,
  onViewSupplyProfile
}: ReceivedOfferCardProps) {
  const [matchOpen, setMatchOpen] = useState(false);
  // bump on checklist change to re-evaluate unlock state
  const [, setProgressTick] = useState(0);
  const showActions = offer.status === 'new';
  const isSaved = offer.status === 'saved';
  const isSucces = offer.status === 'succes';
  const isArchived = offer.status === 'archived';
  const hasListing = !!offer.listingPreview;
  const hasPhotos = !!offer.photos && offer.photos.length > 0;
  const hasMatchDetails = !!offer.matchScore && (offer.matchedFeatures && offer.matchedFeatures.length > 0 || offer.missingFeatures && offer.missingFeatures.length > 0);
  const isUnread = offer.id.startsWith('offer_') && offer.status === 'new';
  // Unlocks the Marchează ca succes CTA
  const checklistComplete = isOfferChecklistComplete(offer.id);
  return <article className={cn('rounded-2xl border transition-colors overflow-hidden bg-dark-2 relative', isSucces ? 'border-lime-4' : isArchived ? 'border-dark-4 opacity-70' : 'border-dark-4 hover:border-light-4')} data-id="element-758">
      {/* Unread dot */}
      {isUnread && <div className="absolute top-4 left-4 w-2.5 h-2.5 rounded-full bg-lime-1 z-10 shadow-[0_0_8px_rgba(204,255,0,0.5)]" data-id="element-759" />}

      {/* Header — about person */}
      <header className={cn('bg-dark-1 px-5 py-4 flex items-start justify-between gap-3', isUnread && 'pl-9')} data-id="element-760">
        <div className="flex items-center gap-3 min-w-0" data-id="element-761">
          {offer.counterpartyAvatar ? <img src={offer.counterpartyAvatar} alt={offer.counterpartyName} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-762" /> : <div className="w-10 h-10 rounded-full bg-dark-3 flex items-center justify-center text-light-2 font-medium shrink-0" data-id="element-763">
              {offer.counterpartyName.charAt(0)}
            </div>}
          <div className="min-w-0" data-id="element-764">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-765">
              <p className="font-semibold text-light-1 truncate" data-id="element-766">
                {offer.counterpartyName}
              </p>
              {offer.matchScore && <button type="button" onClick={e => {
              e.stopPropagation();
              if (hasMatchDetails) setMatchOpen(!matchOpen);
            }} disabled={!hasMatchDetails} className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-3 text-[10px] font-medium text-light-3 whitespace-nowrap transition-colors', hasMatchDetails && 'hover:bg-dark-4 hover:text-light-2 cursor-pointer')} aria-label={`Match score: ${offer.matchScore} facilități`} data-id="element-767">
                  <span data-id="element-768">{offer.matchScore} facilități</span>
                  {hasMatchDetails && <CaretDown size={10} weight="bold" className={cn('transition-transform duration-200', matchOpen && 'rotate-180')} data-id="element-769" />}
                </button>}
            </div>
            <p className="text-xs text-light-4 mt-0.5" data-id="element-770">{offer.sentDate}</p>
          </div>
        </div>

        {/* Status Pill (Receiver view) */}
        <div className="shrink-0" data-id="element-771">
          <OfferStatusPill status={offer.status} variant="receiver" onChange={onStatusChange} data-id="element-772" />
        </div>
      </header>

      {/* Body — about offer */}
      <div className="px-5 py-5 space-y-5" data-id="element-773">
        {/* Match details (expandable) */}
        {hasMatchDetails && matchOpen && <div className="bg-dark-3/50 border border-dark-4 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-774">
            <p className="text-xs font-medium text-light-3 mb-2.5" data-id="element-775">
              Facilități cerute · {offer.matchScore} match
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2" data-id="element-776">
              {offer.matchedFeatures?.map(f => <div key={`m-${f}`} className="flex items-center gap-1.5 text-xs" data-id="element-777">
                  <CheckCircle size={14} weight="fill" className="text-lime-1 shrink-0" data-id="element-778" />
                  <span className="text-light-2 truncate" data-id="element-779">{f}</span>
                </div>)}
              {offer.missingFeatures?.map(f => <div key={`x-${f}`} className="flex items-center gap-1.5 text-xs" data-id="element-780">
                  <Circle size={14} className="text-light-4 shrink-0" data-id="element-781" />
                  <span className="text-light-4 truncate line-through" data-id="element-782">
                    {f}
                  </span>
                </div>)}
            </div>
          </div>}

        {/* Listing preview OR photo grid (XOR) */}
        {hasListing ? <OfferListingPreview {...offer.listingPreview!} data-id="element-783" /> : hasPhotos ? <OfferPhotoGrid photos={offer.photos!} data-id="element-784" /> : null}

        {/* Personal message */}
        <div className="bg-dark-3 p-4 rounded-xl border border-dark-4" data-id="element-785">
          <p className="text-sm text-light-2 italic leading-relaxed" data-id="element-786">
            "{offer.message}"
          </p>
        </div>

        {/* Actions footer — only on 'new' */}
        {showActions && <div className="flex flex-col sm:flex-row gap-3 pt-2" data-id="element-787">
            <button type="button" onClick={onAccept} className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-lime-5 hover:bg-lime-4 border border-lime-4 text-lime-1 transition-colors text-sm font-semibold cursor-pointer" data-id="element-788">
              <Heart size={18} weight="fill" className="shrink-0" data-id="element-789" />
              Salvează
            </button>
            <button type="button" onClick={onPostpone} className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-dark-3 hover:bg-dark-4 text-light-2 transition-colors text-sm font-medium cursor-pointer" data-id="element-790">
              <Clock size={18} weight="regular" className="text-light-3 shrink-0" data-id="element-791" />
              Amână
            </button>
            <button type="button" onClick={onReject} className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-red-3 hover:bg-red-2 border border-red-2 text-red-1 transition-colors text-sm font-medium cursor-pointer" data-id="element-792">
              <XCircle size={18} weight="fill" className="shrink-0" data-id="element-793" />
              Respinge
            </button>
          </div>}

        {/* Saved state */}
        {isSaved && <>
            <div className="bg-lime-5 border border-lime-4 rounded-xl p-4" data-id="element-794">
              <p className="text-sm text-light-1 font-medium mb-1" data-id="element-795">
                Ai salvat oferta de la {offer.counterpartyName}.
              </p>
              <p className="text-xs text-light-3 mb-4" data-id="element-796">
                Proprietarul știe că ești interesat, dar nu are datele tale de
                contact.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-sm" data-id="element-797">
                {offer.contactEmail && <a href={`mailto:${offer.contactEmail}`} className="flex items-center gap-2 text-light-2 hover:text-lime-1 transition-colors" data-id="element-798">
                    <EnvelopeSimple size={16} className="text-lime-1" data-id="element-799" />
                    <span data-id="element-800">{offer.contactEmail}</span>
                  </a>}
                {offer.contactPhone && <a href={`tel:${offer.contactPhone}`} className="flex items-center gap-2 text-light-2 hover:text-lime-1 transition-colors" data-id="element-801">
                    <Phone size={16} className="text-lime-1" data-id="element-802" />
                    <span data-id="element-803">{offer.contactPhone}</span>
                  </a>}
              </div>
            </div>

            {/* Next-steps checklist */}
            <NextStepsChecklist offerId={offer.id} landlordName={offer.counterpartyName} landlordPhone={offer.contactPhone} listingTitle={offer.listingPreview?.title} tenantName={tenantName} onProgressChange={() => setProgressTick(t => t + 1)} data-id="element-804" />

            {/* Closure actions */}
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-805">
              <button type="button" onClick={onMarkSucces} disabled={!checklistComplete} className={cn('w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-sm font-semibold transition-colors', checklistComplete ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} aria-disabled={!checklistComplete} title={checklistComplete ? 'Marchează ca succes' : 'Completează toți pașii din checklist pentru a debloca'} data-id="element-806">
                <Sparkle size={18} weight="fill" className="shrink-0" data-id="element-807" />
                Marchează ca succes
              </button>
              <button type="button" onClick={onReject} className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-dark-3 hover:bg-dark-4 text-light-2 transition-colors text-sm font-medium cursor-pointer" data-id="element-808">
                <XCircle size={18} weight="bold" className="shrink-0" data-id="element-809" />
                Mută la respinse
              </button>
            </div>

            <div className="flex justify-end pt-1" data-id="element-810">
              <button type="button" className="inline-flex items-center gap-1.5 text-xs text-light-4 hover:text-red-1 transition-colors" data-id="element-811">
                <Flag size={14} data-id="element-812" />
                Raportează proprietarul
              </button>
            </div>
          </>}

        {/* Succes terminal state */}
        {isSucces && <div className="bg-lime-5 border border-lime-4 rounded-xl p-4 flex items-start gap-3" data-id="element-813">
            <div className="w-10 h-10 rounded-2xl bg-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-814">
              <Sparkle size={18} weight="fill" className="text-lime-1" data-id="element-815" />
            </div>
            <div className="min-w-0" data-id="element-816">
              <p className="text-sm font-semibold text-light-1" data-id="element-817">
                Te-ai mutat cu{' '}
                {onViewSupplyProfile ? <button type="button" onClick={onViewSupplyProfile} className="underline underline-offset-4 hover:text-lime-1 transition-colors cursor-pointer" data-id="element-818">
                    {offer.counterpartyName}
                  </button> : offer.counterpartyName}
                . 🎉
              </p>
              <p className="text-xs text-light-3 mt-1 leading-relaxed" data-id="element-819">
                Anunțul tău s-a închis automat. Peste un an, te vom invita să
                lași un review.
              </p>
            </div>
          </div>}

        {/* Archived terminal state */}
        {isArchived && <div className="bg-dark-3 border border-dark-4 rounded-xl p-3 text-xs text-light-3" data-id="element-820">
            Această ofertă a fost arhivată automat când ai închis anunțul.
          </div>}

        {/* Rejected state */}
        {offer.status === 'rejected' && <div className="bg-red-4 border border-red-2 rounded-xl p-4" data-id="element-821">
            <p className="text-sm text-light-1 font-medium" data-id="element-822">
              Ai respins această ofertă.
            </p>
            <div className="pt-3 mt-3 border-t border-red-2 flex justify-end" data-id="element-823">
              <button type="button" className="inline-flex items-center gap-1.5 text-xs text-light-4 hover:text-red-1 transition-colors" data-id="element-824">
                <Flag size={14} data-id="element-825" />
                Raportează proprietarul
              </button>
            </div>
          </div>}
      </div>
    </article>;
}